import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import Logo from "../components/Logo";
import { toast } from "sonner";
import {
  ChevronLeft,
  Mail,
  Clock,
  Download,
  Loader2,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { API_BASE } from "../lib/apiBase";

const ADMIN_EMAILS = ["reynir@replydriven.com"];

const AdminEarlyAccess = () => {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  const fetchRows = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/admin/early-access");
      setRows(res.data.requests || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load early access list");
      toast.error("Failed to load early access list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchRows();
  }, [isAdmin]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const bySource = rows.reduce((acc, r) => {
    const key = r.source || "unknown";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const last24 = rows.filter((r) => {
    if (!r.requested_at) return false;
    return (Date.now() - new Date(r.requested_at).getTime()) < 24 * 3600 * 1000;
  }).length;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login?returnTo=/admin/early-access" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo showText linkTo="/" />
            <span className="text-text-muted">/</span>
            <Link to="/admin" className="text-text-muted hover:text-white text-sm">Admin</Link>
            <span className="text-text-muted">/</span>
            <span className="text-white text-sm font-medium">Early Access</span>
          </div>
          <Link to="/admin">
            <Button variant="ghost" className="text-text-muted hover:text-white">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </Link>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                <Sparkles className="w-7 h-7 text-[#D4F34A]" />
                Early Access Requests
              </h1>
              <p className="text-text-muted text-sm mt-1">
                Every email captured through the pre-launch CTA
              </p>
            </div>
            <div className="flex items-center gap-3">
              <a
                href={`${API_BASE}/api/admin/early-access/export.csv`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="early-access-export-csv"
              >
                <Button variant="outline" className="border-[#262626] text-white hover:bg-white/5">
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </a>
              <Button
                onClick={fetchRows}
                disabled={loading}
                variant="outline"
                className="border-[#262626] text-white hover:bg-white/5"
                data-testid="early-access-refresh"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Summary tiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-surface border-[#262626]">
              <CardContent className="py-5">
                <p className="text-sm text-text-muted mb-1">Total</p>
                <p className="text-3xl font-bold text-white" data-testid="early-access-total">
                  {rows.length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-surface border-[#262626]">
              <CardContent className="py-5">
                <p className="text-sm text-text-muted mb-1">Last 24h</p>
                <p className="text-3xl font-bold text-[#D4F34A]">{last24}</p>
              </CardContent>
            </Card>
            <Card className="bg-surface border-[#262626]">
              <CardContent className="py-5">
                <p className="text-sm text-text-muted mb-1">Sources</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {Object.entries(bySource).map(([k, v]) => (
                    <span key={k} className="px-2 py-1 rounded-full bg-[#262626] text-xs text-white">
                      {k}: {v}
                    </span>
                  ))}
                  {Object.keys(bySource).length === 0 && (
                    <span className="text-text-muted text-sm">—</span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {error && (
            <Card className="bg-red-500/10 border-red-500/30 mb-6">
              <CardContent className="py-4">
                <p className="text-red-400">{error}</p>
              </CardContent>
            </Card>
          )}

          {loading && !error && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
            </div>
          )}

          {!loading && !error && rows.length === 0 && (
            <Card className="bg-surface border-[#262626]">
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-[#262626] flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-text-muted" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No early access requests yet</h3>
                <p className="text-text-muted">
                  Emails submitted through the &quot;Request Early Access&quot; CTA will appear here.
                </p>
              </CardContent>
            </Card>
          )}

          {!loading && !error && rows.length > 0 && (
            <Card className="bg-surface border-[#262626]">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="early-access-table">
                    <thead>
                      <tr className="border-b border-[#262626]">
                        <th className="text-left py-4 px-6 text-sm font-medium text-text-muted">Email</th>
                        <th className="text-left py-4 px-4 text-sm font-medium text-text-muted">Requested</th>
                        <th className="text-left py-4 px-4 text-sm font-medium text-text-muted">Source</th>
                        <th className="text-left py-4 px-4 text-sm font-medium text-text-muted">Plan Interest</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr
                          key={`${r.email}-${idx}`}
                          className="border-b border-[#262626] last:border-0 hover:bg-white/5 transition-colors"
                          data-testid={`early-access-row-${idx}`}
                        >
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#262626] flex items-center justify-center">
                                <Mail className="w-4 h-4 text-text-muted" />
                              </div>
                              <span className="text-white font-medium">{r.email}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2 text-text-muted text-sm">
                              <Clock className="w-3.5 h-3.5" />
                              {formatDate(r.requested_at)}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-sm text-text-muted">{r.source || "—"}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-sm text-white">{r.plan_interest || "—"}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminEarlyAccess;
